import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token and optional tenant context to every request automatically.
// sessionStorage wins (impersonation tab) — falls back to localStorage (normal session).
// activeTenantId: set by OwnerDashboard after POST /api/owner/switch — cleared on logout.
// Non-owner users have no activeTenantId in sessionStorage → header omitted → tenantScope
// falls back to req.user.tenantId on the backend (no regression for existing users).
api.interceptors.request.use((config) => {
  const token    = sessionStorage.getItem('pwc_token') || localStorage.getItem('pwc_token');
  const tenantId = sessionStorage.getItem('activeTenantId');
  if (token)    config.headers.Authorization  = `Bearer ${token}`;
  if (tenantId) config.headers['X-Tenant-Id'] = tenantId;
  return config;
});

// Handle token expiry — skip redirect when already on /login so the login page
// can render its own error message instead of reloading and losing state.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      sessionStorage.removeItem('activeTenantId'); // prevent stale owner context on next login
      localStorage.removeItem('pwc_token');
      localStorage.removeItem('pwc_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Users — own profile (stay at /users/*)
export const getNotifications = () => api.get('/users/notifications');
export const markNotificationsRead = () => api.put('/users/notifications/read');

// Admin user management (moved to /admin/users/*)
export const getUsers = (params) => api.get('/admin/users', { params });
export const getUser = (id) => api.get(`/admin/users/${id}`);
export const updateUser = (id, data) => api.put(`/admin/users/${id}`, data);
export const getHealers = () => api.get('/admin/users/healers');
export const getPractitioners = () => api.get('/admin/users/practitioners');
export const deleteUser = (id) => api.delete(`/admin/users/${id}`);
export const getStats = () => api.get('/admin/users/stats/overview');
export const updateHealerVisibility = (id, data) => api.put(`/admin/users/${id}/healer-visibility`, data);
export const getPendingApprovals = () => api.get('/admin/users/pending-approvals');
export const impersonateUser = (id) => api.post(`/admin/users/${id}/impersonate`);
export const approveUser = (id) => api.put(`/admin/users/${id}/approve`);
export const rejectUser = (id) => api.put(`/admin/users/${id}/reject`);
export const getHealerProfile    = (id)       => api.get(`/admin/users/${id}/healer-profile`);
export const updateHealerProfile = (id, data) => api.patch(`/admin/users/${id}/healer-profile`, data);

// S13-S8b — Registration + Referral
export const registerPatient       = (data)       => api.post('/auth/register/patient', data);
export const registerHealer        = (data)       => api.post('/auth/register/healer', data);
export const approveHealer         = (id)         => api.post(`/users/${id}/approve-healer`);
export const rejectHealer          = (id, reason) => api.post(`/users/${id}/reject-healer`, { reason });
export const getCentreReferralLink = (type = 'patient')     => api.get('/centre/referral-link', { params: { type } });
export const getSoloReferralLink   = (type = 'solo_healer') => api.get('/solo/referral-link',   { params: { type } });
export const getMyReferrals        = ()           => api.get('/referrals/my');
export const getPublicCentreInfo   = (code)       => api.get(`/book/${code}`);

// Sessions
export const getSessions = () => api.get('/sessions');
export const getSession = (id) => api.get(`/sessions/${id}`);
export const createSession = (data) => api.post('/sessions', data);
export const updateSession = (id, data) => api.put(`/sessions/${id}`, data);
export const deleteSession = (id) => api.delete(`/sessions/${id}`);
export const getSessionParticipants = (id) => api.get(`/sessions/${id}/participants`);
export const sendSessionAnnouncement = (id) => api.post(`/sessions/${id}/send-announcement`);

// Healing Requests
export const getHealingRequests = () => api.get('/healing-requests');
export const createHealingRequest = (data) => api.post('/healing-requests', data);
export const assignHealer = (id, data) => api.put(`/healing-requests/${id}/assign`, data);
export const addSession = (id, data) => api.put(`/healing-requests/${id}/session`, data);
export const addFeedback = (id, data) => api.put(`/healing-requests/${id}/feedback`, data);
export const getTransferLog = (id) => api.get(`/healing-requests/${id}/transfer-log`);
export const reopenCase = (id, data) => api.put(`/healing-requests/${id}/reopen`, data);
export const returnPatient = (id, data) => api.put(`/healing-requests/${id}/return`, data);
export const returnToHealer = (id, data) => api.put(`/healing-requests/${id}/return-to-healer`, data);
export const dropPatient = (id) => api.put(`/healing-requests/${id}/drop`, { confirm: true });
export const earlyComplete = (id, data) => api.put(`/healing-requests/${id}/early-complete`, data);
export const requestFeedback = (id) => api.put(`/healing-requests/${id}/request-feedback`);
export const submitFeedback = (id, data) => api.put(`/healing-requests/${id}/submit-feedback`, data);
export const requestSessionFeedback = (id, sessionIndex) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/request-feedback`);
export const submitSessionFeedback = (id, sessionIndex, data) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/submit-feedback`, data);
export const recallSessionFeedback = (id, sessionIndex) => api.put(`/healing-requests/${id}/sessions/${sessionIndex}/recall-feedback`);
export const updatePayment = (id, data) => api.put(`/healing-requests/${id}/payment`, data);
export const generatePatientInvoice = (id) => api.post(`/healing-requests/${id}/generate-patient-invoice`);
export const raiseHealerInvoice = (id, data) => api.post(`/healing-requests/${id}/raise-healer-invoice`, data);
export const adminSettlement = (id, data) => api.post(`/healing-requests/${id}/admin-settlement`, data);
export const settleHealerPayment = (id, paymentId, data) => api.put(`/healing-requests/${id}/healer-payments/${paymentId}/settle`, data);

// Announcements
export const getAnnouncementRecipients = (roles) => api.get('/announcements/recipients', { params: { roles: roles.join(',') } });
export const sendAnnouncement = (data) => api.post('/announcements/send', data);
export const getAnnouncementHistory = (params) => api.get('/announcements/history', { params });

// Case Notes
export const addCaseNote = (id, data) => api.post(`/healing-requests/${id}/notes`, data);

// Patient Activities
export const addActivity = (id, data) => api.post(`/healing-requests/${id}/activities`, data);
export const updateActivity = (id, actId, data) => api.put(`/healing-requests/${id}/activities/${actId}`, data);
export const removeActivity = (id, actId) => api.delete(`/healing-requests/${id}/activities/${actId}`);
export const checkInActivity = (id, actId, data) => api.post(`/healing-requests/${id}/activities/${actId}/checkin`, data);

export const updateCaseTags = (id, tags) => api.put(`/healing-requests/${id}/tags`, { tags });
export const setFollowUp = (id, data) => api.put(`/healing-requests/${id}/follow-up`, data);
export const sendHealingReport = (id, data) => api.post(`/healing-requests/${id}/send-report`, data);
export const globalSearch = (q) => api.get('/admin/users/search', { params: { q } });
export const getUserHealingRequests = (id) => api.get(`/admin/users/${id}/healing-requests`);

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const getAuditActors = () => api.get('/audit-logs/actors');

// Course Completions
export const getCourseCompletions = () => api.get('/course-completions');
export const submitCourseCompletion = (data) => api.post('/course-completions', data);
export const verifyCourseCompletion = (id, data) => api.put(`/course-completions/${id}/verify`, data);

// Practitioner Activities
export const getPractitionerActivities = () => api.get('/practitioner-activities');
export const createPractitionerActivity = (data) => api.post('/practitioner-activities', data);
export const updatePractitionerActivity = (id, data) => api.put(`/practitioner-activities/${id}`, data);
export const deletePractitionerActivity = (id) => api.delete(`/practitioner-activities/${id}`);
export const practitionerActivityCheckin = (id, data) => api.post(`/practitioner-activities/${id}/checkin`, data);

// Task 3.9 — voice transcription (SCALE plan). Sends multipart/form-data with audio blob + language hint.
// No Content-Type header — axios auto-sets multipart/form-data with correct boundary when FormData is passed.
// Manually setting Content-Type strips the boundary parameter, causing multer to reject the upload.
export const transcribeAudio = (formData) => api.post('/voice/transcribe', formData, {
  headers: { 'Content-Type': undefined }  // removes instance default so axios auto-sets multipart/form-data boundary
});

// Super Admin — Knowledge Base (Task 3.10)
export const getKnowledgeBase = () => api.get('/superadmin/knowledge-base');
export const getKbSection = (section) => api.get(`/superadmin/knowledge-base/${section}`);
export const updateKbSection = (section, data) => api.put(`/superadmin/knowledge-base/${section}`, data);
export const getKbHistory = (section, language = 'en') => api.get(`/superadmin/knowledge-base/${section}/history`, { params: { language } });

// Super Admin — Platform Overview (Phase 8)
export const getSuperAdminOverview = () =>
  api.get('/superadmin/overview');

// Super Admin — API Cost Monitor (Task 10.9)
export const getApiCosts = (month) =>
  api.get('/superadmin/api-costs', { params: { month } });

// Super Admin — AI Cost Monitor (Phase 10.SA3)
export const getAiCosts = (period, tenantId) =>
  api.get('/superadmin/ai-costs', { params: { period, ...(tenantId ? { tenantId } : {}) } });
export const setAiCostAlert = (payload) =>
  api.post('/superadmin/ai-costs/alerts', payload);

// Super Admin — Finance (Sprint 12-S10)
export const getSuperAdminFinance = () =>
  api.get('/superadmin/finance');

// Super Admin — Platform package stats (14-S10 fix: tenantScope-free endpoint for superadmin)
export const getPackageStatsPlatform = (month) =>
  api.get('/superadmin/finance/package-stats', { params: { month } });

// Payment Analytics — Sprint 13-S7
export const getPaymentAnalyticsSummary  = (month) => api.get('/analytics/payments/summary',   { params: { month } });
export const getPaymentAnalyticsByHealer = (month) => api.get('/analytics/payments/by-healer', { params: { month } });
export const getPaymentAnalyticsPlatform = (month) => api.get('/analytics/payments/platform',  { params: { month } });
export const getPackageStats             = (month) => api.get('/analytics/packages/stats',     { params: { month } }); // 14-S10

// Super Admin — Onboarding Applications (Phase 8)
// status: 'all' | 'pending' | 'approved' | 'rejected'
export const getSuperAdminApplications = (status = 'all') =>
  api.get('/superadmin/applications', { params: { status } });

// PUT — backend uses PUT not POST for approve/reject
export const approveSuperAdminApplication = (id) =>
  api.put(`/superadmin/applications/${id}/approve`);

export const rejectSuperAdminApplication = (id, rejectionReason) =>
  api.put(`/superadmin/applications/${id}/reject`, { rejectionReason });

export const getSoloApplications = (status = 'all') =>
  api.get('/superadmin/solo-applications', { params: { status } });
export const approveSoloApplication = (id) =>
  api.put(`/superadmin/solo-applications/${id}/approve`);
export const rejectSoloApplication = (id, rejectionReason) =>
  api.put(`/superadmin/solo-applications/${id}/reject`, { rejectionReason });

// Super Admin — Pool Healer Approvals
export const getPoolHealerApprovals = (status = 'pending_superadmin') =>
  api.get('/superadmin/healer-approvals', { params: { status } });
export const approvePoolHealer = (id) =>
  api.put(`/superadmin/healer-approvals/${id}/approve`);
export const rejectPoolHealer = (id, reason) =>
  api.put(`/superadmin/healer-approvals/${id}/reject`, { reason });

// Super Admin — Foundations (Task 10.2)
export const getFoundations          = ()         => api.get('/superadmin/foundations');
export const getFoundationById       = (id)       => api.get(`/superadmin/foundations/${id}`);
export const createFoundation        = (data)     => api.post('/superadmin/foundations', data);
export const sendFoundationBroadcast = (id, data) => api.post(`/superadmin/foundations/${id}/broadcast`, data);
export const getFoundationHealers    = (id)       => api.get(`/superadmin/foundations/${id}/healers`);

// Super Admin — Directory Moderation (Task 10.SA4)
export const getDirectoryCentres    = (filter = 'all') => api.get('/superadmin/directory', { params: { filter } });
export const toggleDirectoryListing = (tenantId, isListed) => api.put(`/superadmin/directory/${tenantId}/listing`, { isListed });
export const getDirectoryModerationQueue = () =>
  api.get('/superadmin/directory/moderation');
export const moderateDirectoryTenant = (id, action, note = '') =>
  api.patch(`/superadmin/tenants/${id}/directory`, { action, moderationNote: note });

// Super Admin — Tenant plan management (Task 10.SA2)
export const changeTenantPlan = (id, plan, reason) =>
  api.patch(`/superadmin/tenants/${id}/plan`, { plan, reason });

// Super Admin — Centres page
export const getSuperAdminTenants = (params) =>
  api.get('/superadmin/tenants', { params });
export const assignPatientToTenant = (userId, tenantId) =>
  api.post(`/superadmin/patients/${userId}/assign`, { tenantId });
export const deleteTenant = (id) =>
  api.delete(`/superadmin/tenants/${id}`);
export const patchTenantConfig = (id, config) =>
  api.patch(`/superadmin/tenants/${id}/config`, config);
export const unlockTenant = (id) =>
  api.patch(`/superadmin/tenants/${id}/unlock`);

// Admin — Class & Course Manager (Task 4.6)
export const getClasses    = ()               => api.get('/admin/tenant/classes');
export const saveClasses   = (classSchedule)  => api.put('/admin/tenant/classes', { classSchedule });
export const getCentreProfile = () => api.get('/admin/centre-profile');

// Admin — Holistic Calendar (Phase 11 Task 11.1-G)
export const getHealerSlots     = () => api.get('/admin/healer-slots');
export const getCentreSchedules = () => api.get('/admin/healer-schedule/centre');

// AI Chatbot — Task 4.5
export const chatWithAI = (data) => api.post('/ai/chat', data);

// Owner Dashboard — Task 4.2
export const getOwnerSummary = ()           => api.get('/owner/summary');
export const getOwnerPayouts = ()           => api.get('/owner/payouts');
export const switchCentre    = (tenantId)   => api.post('/owner/switch', { tenantId });

// Super Admin — Users (Sprint 12-S9)
export const getSuperAdminUsers = (params) =>
  api.get('/superadmin/users', { params });
export const impersonateUserSuperAdmin = (id) =>
  api.post(`/superadmin/users/${id}/impersonate`);
export const updateSuperAdminUser = (id, data) =>
  api.patch(`/superadmin/users/${id}`, data);
export const createSuperAdminOwner = (data) =>
  api.post('/superadmin/create-owner', data);

// Super Admin — Owner-centre assignment
export const getSuperAdminOwners    = ()                  => api.get('/superadmin/owners');
export const getSuperAdminOwnerById = (ownerId)           => api.get(`/superadmin/owners/${ownerId}`);
export const addOwnerCentre         = (ownerId, tenantId) => api.post(`/superadmin/owners/${ownerId}/centres/${tenantId}`);
export const removeOwnerCentre      = (ownerId, tenantId) => api.delete(`/superadmin/owners/${ownerId}/centres/${tenantId}`);
export const assignUserAsOwner      = (userId, tenantId)  => api.post('/superadmin/assign-owner', { userId, ...(tenantId && { tenantId }) });

// Super Admin — Centre admin assignment
export const getCentreAdmins   = (tenantId)        => api.get(`/superadmin/centres/${tenantId}/admins`);
export const addCentreAdmin    = (tenantId, userId) => api.post(`/superadmin/centres/${tenantId}/admins`, { userId });
export const removeCentreAdmin = (tenantId, userId) => api.delete(`/superadmin/centres/${tenantId}/admins/${userId}`);

// Super Admin — User centreRoles management (Feature #22)
export const getUserCentreRoles   = (userId)                  => api.get(`/superadmin/users/${userId}/centreRoles`);
export const addUserCentreRole    = (userId, tenantId, role)  => api.post(`/superadmin/users/${userId}/centreRoles`, { tenantId, role });
export const removeUserCentreRole         = (userId, tenantId, role)    => api.delete(`/superadmin/users/${userId}/centreRoles/${tenantId}/${role}`);
export const softDropHealerFromCentre     = (userId, tenantId, remarks) => api.put(`/superadmin/users/${userId}/centreRoles/${tenantId}/drop`, { remarks });
export const overrideHealerDropCooldown   = (userId, tenantId)          => api.patch(`/superadmin/users/${userId}/centreRoles/${tenantId}/override-cooldown`);

// Admin — Assignment Config (Phase 11 Sprint 11-S3)
export const getAssignmentConfig    = ()     => api.get('/admin/assignment-config');
export const updateAssignmentConfig = (data) => api.patch('/admin/assignment-config', data);
export const getAssignmentAnalytics = ()     => api.get('/admin/assignment-analytics');

// Billing — self-serve subscription management (S20)
export const getBillingDashboard = ()           => api.get('/billing/dashboard');
export const subscribePlan       = (planName)   => api.post('/billing/subscribe', { planName });
export const cancelSubscription  = ()           => api.post('/billing/cancel');
export const getPlanOptions      = ()           => api.get('/billing/plan-options');

// Sprint 13-S8 — Patient Onboarding
export const getRegistrationLink = ()        => api.get('/centre/registration-link');
export const sendPatientInvites  = (invites) => api.post('/patients/invite', invites);
export const getPatientInvites   = ()        => api.get('/patients/invites');
export const createPatient       = (data)    => api.post('/patients/create', data);

// Sprint 13-S9 — Healer calendar
export const getHealerCalendar = (month, healerId) =>
  api.get(`/healer/calendar?month=${month}${healerId ? `&healerId=${healerId}` : ''}`);
export const getHealerUpcoming = () => api.get('/healer/calendar/upcoming');

// Promo Codes — Sprint 14-S4
export const createPromo     = (data) => api.post('/promos', data);
export const getPromos       = ()     => api.get('/promos');
export const deactivatePromo = (id)   => api.patch(`/promos/${id}/deactivate`);
export const validatePromo   = (data) => api.post('/promos/validate', data);

// Affinity Insights — Phase 3
export const getAffinityInsights = () => api.get('/admin/affinity-insights');

// Session Payouts — admin list, per-healer summary, and line actions
export const getSessionPayoutLines   = (params) => api.get('/admin/session-payout-lines', { params });
export const getSessionPayoutSummary = ()       => api.get('/admin/session-payout-lines/summary');
export const holdSessionPayoutLine      = (id, data) => api.post(`/admin/session-payout-lines/${id}/hold`, data);
export const releaseSessionPayoutLine   = (id, data) => api.post(`/admin/session-payout-lines/${id}/release`, data);
export const cancelSessionPayoutLine    = (id, data) => api.post(`/admin/session-payout-lines/${id}/cancel`, data);
export const markSessionPayoutLinesPaid = (data)     => api.post('/admin/session-payout-lines/mark-paid', data);
export const getPayoutConfig            = ()         => api.get('/settings/payout-config');
export const putPayoutConfig            = (data)     => api.put('/settings/payout-config', data);

// Package Sales — Sprint 14-S5
export const getPackageSales = (month) =>
  api.get(`/packages/sales${month ? `?month=${month}` : ''}`);

// Packages — patient-level lookup for healer assignment (admin/healer roles)
export const getPatientPackages = (patientId) => api.get(`/packages/patient/${patientId}`);

// Package Management — admin CRUD
export const getPackages       = ()     => api.get('/packages');
export const createPackage     = (data) => api.post('/packages', data);
export const deletePackage     = (id)   => api.delete(`/packages/${id}`);
export const deletePromo       = (id)   => api.delete(`/promos/${id}`);
export const deactivatePackage = (id)   => api.patch(`/packages/${id}/deactivate`);
export const activatePackage   = (id)   => api.patch(`/packages/${id}/activate`);

// Family Group — Sprint 14-S9
export const getFamilyGroup  = ()     => api.get('/family/group');
export const saveFamilyGroup = (data) => api.post('/family/group', data);

// Super Admin — Plan Config (pricing editor)
export const getPlanConfig      = ()             => api.get('/superadmin/plan-config');
export const updatePlanConfig   = (planId, data) => api.put(`/superadmin/plan-config/${planId}`, data);

// Push Campaign — Feature #21 (web)
export const getPushEstimate  = (segment, conditionKeyword) =>
  api.get('/broadcast/push/estimate', { params: { segment, ...(conditionKeyword && { conditionKeyword }) } });
export const getPushHistory   = (page = 1, limit = 20) =>
  api.get('/broadcast/push/history', { params: { page, limit } });
export const sendPushCampaign = (data) => api.post('/broadcast/push', data);

export default api;